/*
 * 模板编辑 Modal
 *
 * 给"我的模板库"卡片的「编辑」按钮用. 两件事:
 *   1. 业务描述 (summary) — 用户业务描述, 展示 + 可编辑
 *   2. 示例文件 (template / filled_sample) — 替换 / 下载, 同名覆盖
 *
 * 不展示:
 *   - display_name / name (技能名由 skill-creator 决定, 用户改坏会乱)
 *   - description (LLM 触发判断用, 用户看了没意义)
 *   - SKILL.md body (内部协议)
 *
 * 文件名做友好别名: template.* → "模板", filled_sample.* → "填写示例".
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Modal, message } from 'antd'
import { xheyAi } from '../api'
import type { SkillItem, SkillReferenceItem } from '../types'
import { trackEnter, trackClick, trackResult } from '../utils/track'
import './EditSkillModal.scss'

function formatBytes(n?: number): string {
  if (!n || n < 0) return ''
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

type RefKind = 'template' | 'sample' | 'generic'

interface RefDisplay {
  kind: RefKind
  display: string
  emoji: string
}

function classifyRef(filename: string): RefDisplay {
  const base = filename.toLowerCase()
  if (/^template\.(xlsx|xls|docx|doc)$/.test(base)) {
    return { kind: 'template', display: '模板', emoji: '📋' }
  }
  if (/^filled[_-]?sample\.(xlsx|xls|docx|doc)$/.test(base)) {
    return { kind: 'sample', display: '填写示例', emoji: '✨' }
  }
  return { kind: 'generic', display: filename, emoji: '📄' }
}

interface Props {
  skill: SkillItem | null
  open: boolean
  onClose: () => void
  /** 用户 save 或替换 reference 后调, 让父组件刷新 skills 列表 */
  onSaved?: () => void
}

export const EditSkillModal: React.FC<Props> = ({
  skill,
  open,
  onClose,
  onSaved
}) => {
  // summary = 给用户看的业务描述. 老 skills 没 summary 时显示空 textarea (不 fallback
  // description), 让用户主动补 — fallback description 含触发词会让用户误以为在改 AI 触发逻辑.
  const [summary, setSummary] = useState('')
  const [refs, setRefs] = useState<SkillReferenceItem[]>([])
  const [loadingRefs, setLoadingRefs] = useState(false)
  const [saving, setSaving] = useState(false)
  const [replacingFilename, setReplacingFilename] = useState<string>('')
  /** 替换过 reference (即时生效, 不依赖保存按钮) → 关闭时触发 onSaved */
  const [refsTouched, setRefsTouched] = useState(false)
  const replaceInputRef = useRef<HTMLInputElement>(null)
  const replaceTargetRef = useRef<string>('')

  useEffect(() => {
    if (!open || !skill) return
    // 进入模板设置半屏埋点(string1=模板 ID)
    trackEnter('templateSetting', {
      requirement_id: 1103,
      track_sign: 'aai.b8136.pageview.ai_enter_web_ai_template_setting',
      string1: skill.name
    })
    setSummary(skill.summary || '')
    setRefs([])
    setRefsTouched(false)
    setLoadingRefs(true)
    let cancelled = false
    xheyAi
      .listSkillReferences(skill.name)
      .then((list) => {
        if (cancelled) return
        setRefs(list)
      })
      .catch((e) => {
        if (cancelled) return
        // eslint-disable-next-line no-console
        console.warn('[EditSkillModal] listSkillReferences failed:', e)
      })
      .finally(() => {
        if (!cancelled) setLoadingRefs(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, skill])

  const handleSave = useCallback(async () => {
    if (!skill) return
    trackClick('templateSetting', 'save', {
      requirement_id: 1103,
      track_sign: 'aai.b8136.click.ai_click_web_ai_template_setting'
    })
    setSaving(true)
    const t0 = Date.now()
    try {
      await xheyAi.updateUserSkill(skill.name, {
        summary: summary.trim()
      })
      // save_template_setting 没文件,string1/number1 按 spec 不上报
      trackResult('templateSetting', 'save_template_setting', {
        requirement_id: 1103,
        track_sign: 'aai.b8136.click.ai_result_web_ai_template_setting',
        actionResult: 'success',
        costTime: Date.now() - t0
      })
      message.success('已保存')
      if (onSaved) onSaved()
      onClose()
    } catch (e) {
      const errMsg = e instanceof Error ? e.message : String(e)
      trackResult('templateSetting', 'save_template_setting', {
        requirement_id: 1103,
        track_sign: 'aai.b8136.click.ai_result_web_ai_template_setting',
        actionResult: 'fail',
        costTime: Date.now() - t0,
        errorMsg: errMsg
      })
      message.error(`保存失败：${errMsg}`)
    } finally {
      setSaving(false)
    }
  }, [skill, summary, onSaved, onClose])

  const handleClose = useCallback(() => {
    // 关闭时如果替换过 reference (即时生效已发生), 也触发父组件刷新
    if (refsTouched && onSaved) onSaved()
    onClose()
  }, [refsTouched, onSaved, onClose])

  const handleReplaceClick = useCallback(
    (filename: string) => {
      // 按文件 kind 区分埋点(template → replace_template_file, sample → replace_sample)
      const kind = classifyRef(filename).kind
      const clickItem =
        kind === 'template'
          ? 'replace_template_file'
          : kind === 'sample'
            ? 'replace_sample'
            : 'replace_template_file'
      trackClick('templateSetting', clickItem, {
        requirement_id: 1103,
        track_sign: 'aai.b8136.click.ai_click_web_ai_template_setting'
      })
      replaceTargetRef.current = filename
      if (replaceInputRef.current) {
        const ext = filename.includes('.')
          ? '.' + filename.split('.').pop()
          : ''
        replaceInputRef.current.accept = ext
        replaceInputRef.current.click()
      }
    },
    [skill]
  )

  const handleReplaceChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const picked = e.target.files?.[0]
      e.target.value = ''
      const targetFilename = replaceTargetRef.current
      replaceTargetRef.current = ''
      if (!picked || !skill || !targetFilename) return
      const renamed = new File([picked], targetFilename, {
        type: picked.type,
        lastModified: picked.lastModified
      })
      setReplacingFilename(targetFilename)
      const t0 = Date.now()
      const kind = classifyRef(targetFilename).kind
      const actionName =
        kind === 'template'
          ? 'replace_template_file'
          : kind === 'sample'
            ? 'replace_sample_file'
            : 'replace_template_file'
      const extRaw = (targetFilename.split('.').pop() || '').toLowerCase()
      const fmt = extRaw === 'docx' || extRaw === 'xlsx' ? extRaw : 'other'
      try {
        await xheyAi.uploadSkillReference(skill.name, renamed)
        trackResult('templateSetting', actionName, {
          requirement_id: 1103,
          track_sign: 'aai.b8136.click.ai_result_web_ai_template_setting',
          actionResult: 'success',
          string1: fmt,
          number1: Math.round(picked.size / 1024),
          costTime: Date.now() - t0
        })
        message.success(`已替换：${classifyRef(targetFilename).display}`)
        setRefsTouched(true)
        const list = await xheyAi.listSkillReferences(skill.name)
        setRefs(list)
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : String(err)
        trackResult('templateSetting', actionName, {
          requirement_id: 1103,
          track_sign: 'aai.b8136.click.ai_result_web_ai_template_setting',
          actionResult: 'fail',
          string1: fmt,
          number1: Math.round(picked.size / 1024),
          costTime: Date.now() - t0,
          errorMsg: errMsg
        })
        message.error(`替换失败：${errMsg}`)
      } finally {
        setReplacingFilename('')
      }
    },
    [skill]
  )

  const skillTitleForHeader =
    (skill && (skill.display_name || skill.name)) || ''

  return (
    <Modal
      open={open}
      title={null}
      footer={null}
      closable={false}
      onCancel={saving ? undefined : handleClose}
      mask={{ closable: !saving }}
      width={580}
      styles={{ body: { padding: 0 } }}
      wrapClassName="aiClerk-skillEdit-modal"
      destroyOnHidden
    >
      <div className="aiClerk-skillEdit">
        <header className="aiClerk-skillEdit__header">
          <div className="aiClerk-skillEdit__headerMain">
            <span className="aiClerk-skillEdit__eyebrow">模板设置</span>
            <h2
              className="aiClerk-skillEdit__title"
              title={skillTitleForHeader}
            >
              {skillTitleForHeader}
            </h2>
          </div>
          <button
            type="button"
            className="aiClerk-skillEdit__close"
            onClick={() => {
              trackClick('templateSetting', 'close', {
                requirement_id: 1103,
                track_sign: 'aai.b8136.click.ai_click_web_ai_template_setting'
              })
              handleClose()
            }}
            disabled={saving}
            aria-label="关闭"
          >
            ×
          </button>
        </header>

        <div className="aiClerk-skillEdit__body">
          {/* ── 段 1: 业务描述 (summary) ─────────────────── */}
          <section className="aiClerk-skillEdit__section">
            <div className="aiClerk-skillEdit__sectionLabel" data-step="1">
              这个模板是干啥的
            </div>
            <textarea
              className="aiClerk-skillEdit__textarea"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder={
                skill && !skill.summary
                  ? '这是老模板, 还未填业务描述. 请用一段话说清楚: 干啥 / 输入什么 / 输出什么 / 适用什么场景'
                  : '一段话说清楚: 干啥 / 输入什么 / 输出什么 / 适用什么场景'
              }
              rows={6}
              maxLength={500}
            />
            <div className="aiClerk-skillEdit__hint">
              这段会展示在卡片和这里, 给你和团队看. 不会影响 AI 触发判断
            </div>
          </section>

          {/* ── 段 2: 示例文件 ──────────────────────────── */}
          <section className="aiClerk-skillEdit__section">
            <div className="aiClerk-skillEdit__sectionLabel" data-step="2">
              示例文件
            </div>

            {loadingRefs ? (
              <div className="aiClerk-skillEdit__refsEmpty">
                正在读取示例文件…
              </div>
            ) : refs.length === 0 ? (
              <div className="aiClerk-skillEdit__refsEmpty">
                这个模板暂时没有示例文件
              </div>
            ) : (
              <div className="aiClerk-skillEdit__refsList">
                {refs.map((r) => {
                  const meta = classifyRef(r.filename)
                  const isReplacing = replacingFilename === r.filename
                  return (
                    <div key={r.filename} className="aiClerk-skillEdit__refRow">
                      <div
                        className={`aiClerk-skillEdit__refIcon aiClerk-skillEdit__refIcon--${meta.kind}`}
                      >
                        {meta.emoji}
                      </div>
                      <div className="aiClerk-skillEdit__refMain">
                        <div
                          className="aiClerk-skillEdit__refName"
                          title={r.filename}
                        >
                          {meta.display}
                        </div>
                        <div className="aiClerk-skillEdit__refMeta">
                          <code>{r.filename}</code>
                          <span>{formatBytes(r.size_bytes)}</span>
                        </div>
                      </div>
                      <div className="aiClerk-skillEdit__refActions">
                        {r.download_url && (
                          <a
                            href={r.download_url}
                            download={r.filename}
                            className="aiClerk-skillEdit__refBtn"
                            onClick={() => {
                              const clickItem =
                                meta.kind === 'template'
                                  ? 'download_template_file'
                                  : meta.kind === 'sample'
                                    ? 'download_sample'
                                    : 'download_template_file'
                              trackClick('templateSetting', clickItem, {
                                requirement_id: 1103,
                                track_sign:
                                  'aai.b8136.click.ai_click_web_ai_template_setting'
                              })
                            }}
                          >
                            下载
                          </a>
                        )}
                        <button
                          type="button"
                          className={`aiClerk-skillEdit__refBtn aiClerk-skillEdit__refBtn--accent${
                            isReplacing
                              ? ' aiClerk-skillEdit__refBtn--loading'
                              : ''
                          }`}
                          onClick={() => handleReplaceClick(r.filename)}
                          disabled={!!replacingFilename}
                        >
                          {isReplacing ? '替换中…' : '替换'}
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
            <input
              ref={replaceInputRef}
              type="file"
              hidden
              onChange={handleReplaceChange}
            />
          </section>
        </div>

        <footer className="aiClerk-skillEdit__footer">
          <button
            type="button"
            className="aiClerk-skillEdit__btnGhost"
            onClick={() => {
              trackClick('templateSetting', 'cancel', {
                requirement_id: 1103,
                track_sign: 'aai.b8136.click.ai_click_web_ai_template_setting'
              })
              handleClose()
            }}
            disabled={saving}
          >
            取消
          </button>
          <button
            type="button"
            className="aiClerk-skillEdit__btnPrimary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? '保存中…' : '保存修改'}
          </button>
        </footer>
      </div>
    </Modal>
  )
}

export default EditSkillModal
